/** @jsxImportSource @emotion/react */
import React, { useState } from 'react'
import { css } from '@emotion/react'
import { InputNumber, Input } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { font, weightMedium, weightSemiBold } from 'src/components/css'
import {
  primaryBadgeStyles,
  warningBadgeStyles,
  textButtonStyles,
} from '../styles/shared'
import type { AiSuggestion } from '../hooks/useAiSuggest'
import { getConstraintDisplayName } from '../constants'

const rowStyles = css`
  display: grid;
  grid-template-columns: 32px 1fr;
  align-items: start;
  gap: 16px;
  padding: 16px;
  border-radius: 8px;
  background-color: ${colors.SECONDARY_400};
  border: 1px solid ${colors.SECONDARY_300};
  transition: all 0.15s ease;

  &:hover {
    border-color: ${colors.PRIMARY};
    background-color: ${colors.SECONDARY_400};
  }
`

/**
 * Custom prominent checkbox column. Larger hit target, visible border in
 * the dark theme, fills with primary color when selected.
 */
const checkboxColumnStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 28px;
`

const customCheckboxStyles = (checked: boolean) => css`
  width: 22px;
  height: 22px;
  min-width: 22px;
  border-radius: 4px;
  border: 2px solid ${checked ? colors.PRIMARY : colors.NEUTRAL_70};
  background-color: ${checked ? colors.PRIMARY : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;

  &:hover {
    border-color: ${colors.PRIMARY};
    background-color: ${checked ? colors.PRIMARY : `${colors.PRIMARY}1A`};
  }
`

const checkmarkStyles = css`
  color: ${colors.PRIMARY_TEXT};
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
`

/**
 * Right column wrapper — holds title, rationale, and actions.
 */
const rowBodyStyles = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const rowHeaderStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`

const titleStyles = css`
  ${font.body};
  font-weight: ${weightSemiBold};
  font-size: 14px;
  color: ${colors.WHITE};
  display: flex;
  align-items: center;
  gap: 8px;
`

const rationaleStyles = css`
  ${font.body};
  font-size: 12px;
  color: ${colors.NEUTRAL_70};
  line-height: 1.5;
  white-space: pre-wrap;
`

const actionsStyles = css`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
`

const editFormStyles = css`
  background-color: ${colors.SECONDARY_300};
  border-radius: 6px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const labelStyles = css`
  ${font.body};
  font-size: 11px;
  font-weight: ${weightMedium};
  color: ${colors.NEUTRAL_70};
  text-transform: uppercase;
`

interface AiSuggestRowProps {
  suggestion: AiSuggestion
  /** Bulk-select state — required. Each row is a checkbox + edit-only. */
  bulkSelected: boolean
  onBulkSelectedChange: (id: string, selected: boolean) => void
  /** Optional editor — fires whenever the user changes the inline param form. */
  onEditedParamsChange?: (
    id: string,
    editedParams: Record<string, unknown> | null
  ) => void
  disabled?: boolean
}

/**
 * One suggestion row in the AI Suggest modal.
 *
 * UX simplification (post Round-3): no per-row Accept/Reject buttons.
 * Each row is a checkbox + (optional) "Edit params" inline editor.
 * Acceptance happens exclusively through the modal's "Accept selected"
 * button. Closing the modal without selecting = no action (suggestions
 * persist server-side until they expire at created_at + 24h, then they
 * become `expired` rows for the trainer).
 *
 * Phase 1: the inline editor handles the most common parameter shapes
 * (numeric / time-window). Phase 2+ will swap this for the shared
 * AddConstraintModal parameter renderers.
 */
const AiSuggestRow: React.FC<AiSuggestRowProps> = ({
  suggestion,
  bulkSelected,
  onBulkSelectedChange,
  onEditedParamsChange,
  disabled = false,
}) => {
  'use memo'

  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [editedParams, setEditedParams] = useState<Record<string, unknown>>(
    () => ({ ...(suggestion.parameters ?? {}) })
  )

  const isHard = suggestion.is_hard ?? false
  const hardSoftLabel = isHard ? 'HARD' : 'SOFT'

  const displayName = getConstraintDisplayName(
    suggestion.type_code,
    suggestion.type_code
  )

  // Notify parent when edits change so bulk-accept can pick them up.
  const updateEditedParams = (next: Record<string, unknown>) => {
    setEditedParams(next)
    if (onEditedParamsChange) {
      onEditedParamsChange(suggestion.id, next)
    }
  }

  const toggleEditing = () => {
    const nowEditing = !isEditing
    setIsEditing(nowEditing)
    // When the user closes the editor without changes, clear the
    // edited-params record so bulk-accept submits the suggestion
    // unchanged.
    if (!nowEditing && onEditedParamsChange) {
      onEditedParamsChange(suggestion.id, null)
    }
  }

  const renderEditFields = () => {
    // Phase 1: minimal field renderers covering the 5 canned types.
    // Phase 2 swaps this for the shared parameterFields component.
    switch (suggestion.type_code) {
      case 'min_rest_days':
        return (
          <div css={editFormStyles}>
            <span css={labelStyles}>Minimum rest days</span>
            <InputNumber
              min={0}
              max={14}
              value={(editedParams.min_days as number) ?? 1}
              onChange={(v) =>
                updateEditedParams({ ...editedParams, min_days: v ?? 0 })
              }
            />
          </div>
        )
      case 'no_back_to_back':
        return (
          <div css={editFormStyles}>
            <span css={labelStyles}>Minimum minutes between games</span>
            <InputNumber
              min={0}
              max={4320}
              step={30}
              value={(editedParams.min_minutes_between as number) ?? 1440}
              onChange={(v) =>
                updateEditedParams({
                  ...editedParams,
                  min_minutes_between: v ?? 0,
                })
              }
            />
          </div>
        )
      case 'bye_distribution':
        return (
          <div css={editFormStyles}>
            <span css={labelStyles}>Max consecutive byes</span>
            <InputNumber
              min={0}
              max={10}
              value={(editedParams.max_consecutive_byes as number) ?? 2}
              onChange={(v) =>
                updateEditedParams({
                  ...editedParams,
                  max_consecutive_byes: v ?? 0,
                })
              }
            />
          </div>
        )
      case 'home_away_balance':
        return (
          <div css={editFormStyles}>
            <span css={labelStyles}>Target home percentage</span>
            <InputNumber
              min={0}
              max={100}
              value={(editedParams.target_home_percentage as number) ?? 50}
              onChange={(v) =>
                updateEditedParams({
                  ...editedParams,
                  target_home_percentage: v ?? 50,
                })
              }
            />
          </div>
        )
      case 'time_window':
        return (
          <div css={editFormStyles}>
            <span css={labelStyles}>Start time (HH:MM)</span>
            <Input
              value={(editedParams.start_time as string) ?? '00:00'}
              onChange={(e) =>
                updateEditedParams({
                  ...editedParams,
                  start_time: e.target.value,
                })
              }
              maxLength={5}
            />
            <span css={labelStyles}>End time (HH:MM)</span>
            <Input
              value={(editedParams.end_time as string) ?? '23:59'}
              onChange={(e) =>
                updateEditedParams({
                  ...editedParams,
                  end_time: e.target.value,
                })
              }
              maxLength={5}
            />
          </div>
        )
      default:
        return (
          <div css={editFormStyles}>
            <span css={labelStyles}>
              Phase 1: this suggestion type uses the unchanged parameters.
              Editing this type will land in Phase 2.
            </span>
          </div>
        )
    }
  }

  return (
    <label
      css={css`
        ${rowStyles};
        cursor: pointer;
        ${bulkSelected
          ? `border-color: ${colors.PRIMARY}; background-color: ${colors.PRIMARY}0D;`
          : ''}
      `}
      data-testid={`ai-suggest-row-${suggestion.id}`}
    >
      {/* Left column: prominent checkbox, vertically centered with the title. */}
      <div css={checkboxColumnStyles}>
        <span css={customCheckboxStyles(bulkSelected)} aria-hidden='true'>
          {bulkSelected && <span css={checkmarkStyles}>✓</span>}
        </span>
        <input
          type='checkbox'
          checked={bulkSelected}
          onChange={(e) =>
            onBulkSelectedChange(suggestion.id, e.target.checked)
          }
          disabled={disabled}
          aria-label={`Select ${displayName}`}
          // Visually hidden — the styled <span> above is the visible UI.
          // Native checkbox stays in the DOM for keyboard accessibility.
          css={css`
            position: absolute;
            opacity: 0;
            width: 0;
            height: 0;
            pointer-events: none;
          `}
        />
      </div>

      {/* Right column: title, rationale, edit form, actions. */}
      <div css={rowBodyStyles}>
        <div css={rowHeaderStyles}>
          <span css={titleStyles}>
            {displayName}
            <span css={isHard ? warningBadgeStyles : primaryBadgeStyles}>
              {hardSoftLabel}
            </span>
          </span>
        </div>

        {suggestion.rationale && (
          <p css={rationaleStyles}>{suggestion.rationale}</p>
        )}

        {isEditing && renderEditFields()}

        <div css={actionsStyles}>
          <button
            type='button'
            css={textButtonStyles}
            onClick={(e) => {
              // Stop the click bubbling to the wrapping <label>, which
              // would otherwise toggle the checkbox.
              e.preventDefault()
              e.stopPropagation()
              toggleEditing()
            }}
            disabled={disabled}
          >
            {isEditing ? 'Cancel edit' : 'Edit params'}
          </button>
        </div>
      </div>
    </label>
  )
}

export default AiSuggestRow
